import { URLSearchParams } from 'url';

import * as jose from 'jose';
import { NextRequest } from 'next/server';

const confIssuer = process.env.AUTH_CLIENT_ISSUER as string;
const provider = process.env.AUTH_CLIENT_PROVIDER as string;
const issuer = provider === 'okta'
  ? confIssuer
  : `${confIssuer}/`;
const audience = process.env.AUTH_CLIENT_AUDIENCE_BROWSER as string;
const jwks = process.env.AUTH_CLIENT_JWKS_URI as string;

export const baseApiUrl = process.env.ZEUS_API_URL as string;
export const baseTenantsApiUrl = `${baseApiUrl}/v1/tenants`;
export const baseTenantsApiUrlV2 = `${baseApiUrl}/v2/organizations`;
export const baseTenantsApiUrlV3 = `${baseApiUrl}/v3/organizations`;
export const baseTenantsApiUrlV4 = `${baseApiUrl}/v4/organizations`;

/**
 * Verify token on the Edge Environment
 * (used jose instead of jsonwebtoken because jsonwebtoken cannot run on the Edge environment)
 *
 * @param token the token needs to be verify
 * @returns jwt verified result and resolved key
 * @see [More Info](https://github.com/vercel/next.js/discussions/38227#discussioncomment-3063432)
 */
export const verifyToken = async (token: string) => {
  const JWKS = jose.createRemoteJWKSet(new URL(jwks));

  return jose.jwtVerify(token, JWKS, {
    issuer,
    audience: [audience],
  });
};

/**
 * Check if the header has the token and if the token is valid, use when the route should be behind auth
 *
 * @param req the token needs to be verify
 * @returns true if the header has a token and is valid
 */
export const hasAccess = async (req: NextRequest) => {
  const token = req.headers.get('Authorization')?.split(' ')?.[1];

  if (!token) {
    return false;
  }

  try {
    await verifyToken(token as string);

    return true;
  } catch {
    return false;
  }
};

/**
 * Constructs a valid URL string by combining a base path with query parameters
 * 
 * @param basePath - The base URL path to build upon. Can start with or without forward slash
 * @param params - An object containing key-value pairs to be converted to URL query parameters
 * @returns A properly formatted relative URL string with pathname and search parameters
 * @throws {Error} If URL construction fails due to invalid parameters
 * 
 * @example
 * ```typescript
 * buildValidUrl('/api/v1/data', { limit: 10, sort: 'desc' })
 * // Returns: '/api/v1/data?limit=10&sort=desc'
 * ```
 */
export function buildValidUrl(
  request: NextRequest,
  basePath: string,
  params: Record<string, string | boolean | number>,
): URL {
  const base = `https://${request.headers.get('host')}`;
  // Handle basePath format
  const normalizedPath = (basePath.startsWith('http') || basePath.startsWith('/')) ? basePath : `/${basePath}`;

  // Convert params to URLSearchParams
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    searchParams.append(key, value.toString());
  });

  try {
    // Construct full URL with current origin
    const url = new URL(normalizedPath, base);
    url.search = searchParams.toString();

    // Return just the pathname + search to keep it relative
    return url;
  } catch (error) {
    console.error('URL construction failed:', error);
    const errorMessage = error instanceof Error ? error.message : 'unknown error';
    throw new Error(`Invalid URL parameters: ${errorMessage}`);
  }
}
